import type {ComponentProps} from 'react';

import {
  AttachmentMessageRow,
  ButtonOptionsMessageRow,
  InlineDataMessageRow,
  type MessageComponentRegistry,
  RichTextMessageRow,
  TextMessageRow,
  TextWithAttachmentsMessageRow,
} from '@stripe-internal/conversations-ui';

import {ChatBubbleDotCom} from './Chat/ChatBubbleDotCom';
import {ChatButtonOptions} from './Chat/ChatButtonOptions';
import {ChatRow} from './Chat/ChatRow';
import {DotComMessageButton} from './Chat/DotComMessageButton';
import {LiveAnswersRowDotCom} from './Chat/LiveAnswersRowDotCom';
import {TextRow} from './Chat/TextRow';

const StaticChatBubble = (
  props: Omit<ComponentProps<typeof ChatBubbleDotCom>, 'animation'>,
) => <ChatBubbleDotCom {...props} animation="disabled" />;

const StaticTextRow = (props: ComponentProps<typeof TextRow>) => (
  <TextRow {...props} streaming={false} />
);

const DotComTextMessageRow: NonNullable<
  MessageComponentRegistry['text-conversation-message']
> = (props) => <TextMessageRow {...props} TextRowComponent={TextRow} />;

const DotComButtonOptionsMessageRow: NonNullable<
  MessageComponentRegistry['button-options-conversation-message']
> = (props) => (
  <ButtonOptionsMessageRow
    {...props}
    ChatRowComponent={ChatRow}
    ChatButtonOptionsComponent={ChatButtonOptions}
  />
);

const DotComRichTextMessageRow: NonNullable<
  MessageComponentRegistry['rich-text-conversation-message']
> = (props) => (
  <RichTextMessageRow
    {...props}
    TextRowComponent={TextRow}
    MessageButtonComponent={DotComMessageButton}
    buttonsPlacement="after-text"
  />
);

const DotComAttachmentMessageRow: NonNullable<
  MessageComponentRegistry['attachment-conversation-message']
> = (props) => (
  <AttachmentMessageRow
    {...props}
    ChatRowComponent={ChatRow}
    ChatBubbleComponent={StaticChatBubble}
  />
);

const DotComTextWithAttachmentsMessageRow: NonNullable<
  MessageComponentRegistry['text-with-attachments-conversation-message']
> = (props) => (
  <TextWithAttachmentsMessageRow
    {...props}
    TextRowComponent={StaticTextRow}
    ChatRowComponent={ChatRow}
    ChatBubbleComponent={StaticChatBubble}
  />
);

const DotComInlineDataMessageRow: NonNullable<
  MessageComponentRegistry['inline-data-conversation-message']
> = (props) => (
  <InlineDataMessageRow
    {...props}
    ChatRowComponent={ChatRow}
    ChatBubbleComponent={StaticChatBubble}
    LiveAnswersRowComponent={LiveAnswersRowDotCom}
  />
);

export const DotComMessageComponents = {
  'text-conversation-message': DotComTextMessageRow,
  'button-options-conversation-message': DotComButtonOptionsMessageRow,
  'rich-text-conversation-message': DotComRichTextMessageRow,
  'attachment-conversation-message': DotComAttachmentMessageRow,
  'text-with-attachments-conversation-message':
    DotComTextWithAttachmentsMessageRow,
  'inline-data-conversation-message': DotComInlineDataMessageRow,
} satisfies MessageComponentRegistry;
